import React, { useEffect } from 'react'

// components
import { LoaderSpinner } from '@/components/ui/LoaderSpinner.component'
import { useLoadScript, Autocomplete } from '@react-google-maps/api'
import { useGeoLocation } from '@/shared/hooks/geoLocation'
import { formatLocation } from '@/shared/utils/pipes'
import { environment } from '@/shared/consts/environment'

// styles
import {
  inputContainer,
  labelInput,
  inputContainerField,
  inputLocationIcon,
  inputLocationField,
  inputFieldError,
  loaderContainer,
  errorMessage,
  inputFieldWithPlaceHolderActive,
} from '../styles'
import { cssClass } from '@/shared/utils/styles'
import { LocationIcon } from 'public/assets/v2/images/icons'
import { SizeInput } from './interfaces'

interface Props {
  label?: string
  size?: SizeInput
  placeholder?: string
  value?: string | number
  name?: string
  error?: string
  disabled?: boolean
  required?: boolean
  className?: string[]
  containerClassName?: string[]
  loading?: boolean
  auto?: boolean
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  onLocationValidated?: (isValid: boolean) => void
}

type Library =
  | 'places'
  | 'drawing'
  | 'geometry'
  | 'localContext'
  | 'visualization'

const LIBS: Library[] = ['places']

export const InputLocationComponent: React.FC<Props> = ({
  label,
  placeholder,
  value,
  name,
  error,
  className = [],
  containerClassName = [],
  disabled = false,
  required = false,
  loading = false,
  auto = false,
  onFocus,
  onChange,
  onBlur,
  onLocationValidated,
}) => {
  const mapRef = React.useRef<Autocomplete>(null)
  const elementRef = React.useRef<HTMLInputElement>(null)
  const { location } = useGeoLocation() || {}
  const [inputAutoSent, setInputAutoSent] = React.useState(false)
  const [wasAutoCompleted, setWasAutoCompleted] = React.useState(false)

  useEffect(() => {
    if (wasAutoCompleted) {
      setTimeout(() => {
        setWasAutoCompleted(false)
      }, 600)
    }
  }, [wasAutoCompleted])

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: environment.googleMapsApiKey,
    libraries: LIBS,
  })

  const autocompleteService = React.useMemo(
    () => isLoaded && new window.google.maps.places.AutocompleteService(),
    [isLoaded]
  )

  const validateLocation = (inputValue: string) => {
    if (!autocompleteService) {
      return
    }

    autocompleteService.getPlacePredictions(
      { input: String(value) },
      (predictions, status) => {
        let exists
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          exists = predictions.find(
            (prediction) => prediction.description === inputValue
          )
        }

        onLocationValidated && onLocationValidated(!!exists)
      }
    )
  }

  const onBlurInput = (e: React.FocusEvent<HTMLInputElement>) => {
    onBlur && onBlur(e)
    if (!wasAutoCompleted) {
      setTimeout(() => {
        validateLocation(e.target.value)
      }, 400)
    }

    setWasAutoCompleted(false)
    // validateLocation(e.target.value)
  }

  const changeValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange && onChange(e)
  }

  const onMapChanged = () => {
    const inputValue = elementRef.current?.value
    setWasAutoCompleted(true)
    onChange &&
      onChange({
        target: { value: inputValue, name: name },
      } as React.ChangeEvent<HTMLInputElement>)
  }

  React.useEffect(() => {
    if (location && onChange && auto && !inputAutoSent) {
      onChange({
        target: { value: formatLocation(location) },
      } as React.ChangeEvent<HTMLInputElement>)

      setInputAutoSent(true)
    }
  }, [location, onChange, auto, inputAutoSent])

  if (!isLoaded) return <div>Loading...</div>

  const hasValue = value && value !== ''
  const inputFieldWithPlaceHolder =
    hasValue && placeholder ? inputFieldWithPlaceHolderActive : ''

  const paddingY = 'pt-14 pb-14'
  const paddingL = 'pl-44'
  const paddingR = 'pr-16'

  return (
    <div className={cssClass([inputContainer, ...containerClassName])}>
      <label className={labelInput} htmlFor={name}>
        {label}
      </label>
      <div className={inputContainerField}>
        <div className={cssClass([inputLocationIcon, 'z-10'])}>
          <LocationIcon className="text-tertirary" />
        </div>

        <Autocomplete
          ref={mapRef}
          types={['(cities)']}
          onPlaceChanged={onMapChanged}
        >
          <input
            ref={elementRef}
            type="text"
            name={name}
            value={value}
            placeholder={placeholder}
            onChange={changeValue}
            disabled={disabled || loading}
            required={required}
            className={cssClass([
              inputLocationField,
              inputFieldWithPlaceHolder,
              error ? inputFieldError : '',
              paddingY,
              paddingL,
              paddingR,
              ...className,
            ])}
            onFocus={onFocus}
            onBlur={onBlurInput}
          />
        </Autocomplete>
        {loading && (
          <div className={loaderContainer}>
            <LoaderSpinner size={15} background="rgba(255, 255, 255, 0.4)" />
          </div>
        )}
      </div>
      {error && <span className={errorMessage}>{error}</span>}
    </div>
  )
}
